/**
 * Household feature hooks.
 */

import React, { useState, useCallback, useEffect, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';
import { User } from '@/infra/types/domain';
import { selectHouseholdUsers } from '@/features/household/selectors';

/** Stable empty array to avoid useSyncExternalStore re-renders when no Provider is present. */
const emptyUsers: User[] = [];

/**
 * Resolve household members from Redux store (reactive subscription) or fetch (async fallback).
 *
 * When a Redux Provider is present, subscribes to store changes via selectHouseholdUsers
 * so the component re-renders when household.users is updated (e.g. by an async saga).
 * Falls back to a /api/household/members fetch when no Redux context is available.
 */
export function useHouseholdMembers(): User[] {
  const reduxContext = React.useContext(ReactReduxContext);

  // Redux path: use useSyncExternalStore for tearing prevention in React 18 Concurrent Mode
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!reduxContext) return () => {};
      return reduxContext.store.subscribe(onStoreChange);
    },
    [reduxContext],
  );

  const getSnapshot = useCallback(() => {
    if (!reduxContext) return emptyUsers;
    return selectHouseholdUsers(reduxContext.store.getState());
  }, [reduxContext]);

  const reduxMembers = useSyncExternalStore(subscribe, getSnapshot);

  // Fetch-backed state — used only when Redux context is unavailable
  const [fetchedMembers, setFetchedMembers] = useState<User[]>([]);

  // Fetch fallback for non-Redux contexts (e.g. standalone rendering)
  useEffect(() => {
    if (reduxContext) return;
    if (typeof fetch === 'undefined') return;
    fetch('/api/household/members')
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setFetchedMembers(data.data || []))
      .catch(() => {});
  }, [reduxContext]);

  return reduxContext ? reduxMembers : fetchedMembers;
}
